import * as Actions from '../actions';
import { actionFetchProfileList } from '@redact/state/modules/settingsProfile/actions';
import * as Services from '../services';
import {
  all,
  fork,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { sagaIntl } from '@i18n';
import {
  FetchProfileListResponse,
  SettingsProfileType,
  fetchProfileListPayload,
} from '../models';
import { PayloadAction } from '@reduxjs/toolkit';
import { selectAllProfileRecords } from '../selectors';
import { selectUser } from '@helpers/tdoHelper';

export default function* defaultGlobalSettingsSaga() {
  yield* all([
    fork(watchFetchProfileList),
    fork(watchDeleteProfile),
    fork(watchRestoreProfile),
    fork(watchSetProfileDeleted),
    fork(watchPermanentlyDeleteProfile),
    fork(watchUpdateProfile),
    fork(watchCreateProfile),
  ]);
}

function* watchFetchProfileList() {
  yield* all([
    takeLatest(Actions.FETCH_PROFILE_LIST_REQUEST, fetchProfileListRequest),
    takeLatest(Actions.FETCH_PROFILE_LIST_SUCCESS, fetchProfileListSuccess),
    takeLatest(Actions.FETCH_PROFILE_LIST_FAILURE, fetchProfileListFailure),
  ]);
}

function* fetchProfileListRequest(
  action: PayloadAction<{ limit: number; offset: number }>
) {
  const { limit, offset } = action.payload;
  yield* put(Services.fetchProfileList(limit, offset));
}

function* fetchProfileListSuccess(
  action: PayloadAction<
    FetchProfileListResponse,
    any,
    { variables: fetchProfileListPayload }
  >
) {
  const { count } = action.payload.structuredDataObjects;
  const { limit, offset } = action.meta.variables;
  if (count === limit) {
    yield* put(Actions.FETCH_PROFILE_LIST_REQUEST({ limit, offset }));
  }
}

function* fetchProfileListFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'fetchProfileListFailure',
        defaultMessage:
          'Unexpected error occurred while fetching profile list.',
      }),
      variant: 'error',
    })
  );
}

function* watchDeleteProfile() {
  yield* all([
    takeLatest(
      Actions.DELETE_SETTINGS_PROFILE_REQUEST,
      deleteSettingsProfileRequest
    ),
    takeLatest(
      Actions.DELETE_SETTINGS_PROFILE_FAILURE,
      deleteSettingsProfileFailure
    ),
  ]);
}

/**
 * Set `isDeleted` on the profile rather than removing it.
 *
 * The update mutation is an upsert keyed by id, so this is the same write the
 * edit panel makes — nothing here needs the delete mutation, which is now
 * reserved for the permanent delete in the deleted view.
 */
function* setProfileDeleted(profileId: string, isDeleted: boolean) {
  const intl = sagaIntl();
  const profiles = yield* select(selectAllProfileRecords);
  const profile = profiles.find((p) => p.id === profileId);
  if (!profile) {
    // The user has just confirmed a destructive action; saying nothing is
    // indistinguishable from a hung request.
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'profileNoLongerAvailable',
          defaultMessage: 'That profile is no longer available.',
        }),
        variant: 'error',
      })
    );
    return;
  }
  const currentUser = yield* select(selectUser);
  yield* put(
    Services.setSettingsProfileDeleted({
      ...profile,
      data: {
        ...profile.data,
        isDeleted,
        // Deleting the default must surrender the flag. It used to survive,
        // invisible to `changeDefaultSettingsProfile` (which only clears the
        // prior default among LIVE rows), so a restore produced a second
        // `isDefault: true` and `selectDefaultProfile` — which seeds the
        // settings for newly ingested media — resolved by fetch order.
        ...(isDeleted ? { isDefault: false } : {}),
        // Stamped like every other write in this module. The record now
        // survives the delete, so an unstamped one shows a stale actor
        // beside a fresh timestamp, and these objects have no other trail.
        modifiedBy: currentUser?.email ?? profile.data.modifiedBy,
      },
    })
  );
}

function* deleteSettingsProfileRequest(
  action: PayloadAction<{ profileId: string }>
) {
  yield* setProfileDeleted(action.payload.profileId, true);
}

function* watchSetProfileDeleted() {
  yield* all([
    takeLatest(Actions.SET_SETTINGS_PROFILE_DELETED_SUCCESS, function* () {
      yield* put(actionFetchProfileList({ limit: 1000, offset: 0 }));
    }),
    takeLatest(Actions.SET_SETTINGS_PROFILE_DELETED_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'deleteSettingsProfileFailure',
            defaultMessage:
              'Unexpected error occurred while deleting settings profile.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}

function* watchRestoreProfile() {
  yield* all([
    takeLatest(
      Actions.RESTORE_SETTINGS_PROFILE_REQUEST,
      restoreSettingsProfileRequest
    ),
  ]);
}

function* restoreSettingsProfileRequest(
  action: PayloadAction<{ profileId: string }>
) {
  yield* setProfileDeleted(action.payload.profileId, false);
}

function* watchPermanentlyDeleteProfile() {
  yield* all([
    takeLatest(
      Actions.PERMANENTLY_DELETE_SETTINGS_PROFILE_REQUEST,
      permanentlyDeleteSettingsProfileRequest
    ),
  ]);
}

/** The only path that actually removes the record. */
function* permanentlyDeleteSettingsProfileRequest(
  action: PayloadAction<{ profileId: string }>
) {
  yield* put(Services.deleteSettingsProfile(action.payload.profileId));
}

function* deleteSettingsProfileFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'deleteSettingsProfileFailure',
        defaultMessage:
          'Unexpected error occurred while deleting settings profile.',
      }),
      variant: 'error',
    })
  );
}

function* watchUpdateProfile() {
  yield* all([
    takeEvery(
      Actions.UPDATE_SETTINGS_PROFILE_REQUEST,
      updateSettingsProfileRequest
    ),
    takeEvery(
      Actions.UPDATE_SETTINGS_PROFILE_FAILURE,
      updateSettingsProfileFailure
    ),
  ]);
}

function* updateSettingsProfileRequest(
  action: PayloadAction<{ profile: SettingsProfileType }>
) {
  const { profile } = action.payload;
  yield* put(Services.updateSettingsProfile(profile));
}

function* updateSettingsProfileFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'updateSettingsProfileFailure',
        defaultMessage:
          'Unexpected error occurred while updating settings profile.',
      }),
      variant: 'error',
    })
  );
}

function* watchCreateProfile() {
  yield* all([
    takeLatest(
      Actions.CREATE_SETTINGS_PROFILE_REQUEST,
      createSettingsProfileRequest
    ),
    takeLatest(
      Actions.CREATE_SETTINGS_PROFILE_SUCCESS,
      createSettingsProfileSuccess
    ),
    takeLatest(
      Actions.CREATE_SETTINGS_PROFILE_FAILURE,
      createSettingsProfileFailure
    ),
  ]);
}

function* createSettingsProfileRequest(
  action: PayloadAction<{ data: SettingsProfileType['data'] }>
) {
  const { data } = action.payload;
  yield* put(Services.createSettingsProfile(data));
}

function* createSettingsProfileSuccess() {
  yield* put(actionFetchProfileList({ limit: 1000, offset: 0 }));
}

function* createSettingsProfileFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'createSettingsProfileFailure',
        defaultMessage:
          'Unexpected error occurred while creating settings profile.',
      }),
      variant: 'error',
    })
  );
}
